import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SideBarContent, MainContent } from './components';

import { Divider, Loader } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { DefaultTabsProps, Tabs } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import { useMetaData } from '@/hooks/useMetaData';
import { useGetFullInitData } from '@/hooks/useTda';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { getInitData, getPreviousTourneys } from '@/services/tda';
import { Maybe } from '@/types';
import {
  ParticipandoTda,
  PendientesTda,
  ResponseInitTDA,
  TorneoAnterior,
  TorneosSugeridos,
} from '@/types/tda';
import { GENERICS_WORDS, TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface State {
  title: string;
  init: Maybe<ResponseInitTDA>;
  recommendedList: TorneosSugeridos[];
  pendingList: PendientesTda[];
  playingList: ParticipandoTda[];
  previousTourneys: TorneoAnterior[];
  mounted: boolean;
}

const initialState: State = {
  init: null,
  recommendedList: [],
  pendingList: [],
  playingList: [],
  title: '',
  previousTourneys: [],
  mounted: false,
};

export const HomeTDA = () => {
  const navigate = useNavigate();
  const { userInitialData: initData } = useAuth();
  const { data, isLoading, isRefetching } = useGetFullInitData();
  const [state, setState] = useState<State>(initialState);
  const toast = useToast();
  const { handleTabChanges, tab } = useHandleChangesTabs();
  const { tourneyData, handleSetTourney, handleResetTourney } = useTourney();
  const isLastMatchDay = useIsLastMatchDay();

  // reset tourney data on mount
  useEffect(() => {
    handleResetTourney();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMetaData({
    title: 'Torneos de Amigos',
  });

  const getPrevious = useCallback(async () => {
    if (state.previousTourneys.length === 0) {
      try {
        const response = await getPreviousTourneys();
        if (response.estado) {
          setState((prevState) => ({
            ...prevState,
            previousTourneys: response.torneos_amigo_ant || [],
          }));
        }
      } catch (error) {
        console.error('Error obteniendo torneos anteriores:', error);
      }
    }
  }, [state.previousTourneys]);

  useEffect(() => {
    if (
      data?.init.estado &&
      data?.recommended.estado &&
      !state.mounted &&
      !isLoading &&
      !isRefetching
    ) {
      const { init, recommended } = data;

      setState((prevState) => ({
        ...prevState,
        init,
        recommendedList: recommended.torneosSugeridos || [],
        pendingList: init.torneos_amigo.pendientesAcDuenio || [],
        playingList: init.torneos_amigo.participando || [],
        title: GENERICS_WORDS.tdaClassic,
        mounted: true,
      }));

      handleSetTourney({
        ...tourneyData,
        tourneysPlaying: init.torneos_amigo.participando || [],
        tourneysPending: init.torneos_amigo.pendientesAcDuenio || [],
      });

      if (init.cantTasAnteriores > 0) {
        getPrevious();
      }
    }
  }, [
    data,
    getPrevious,
    handleSetTourney,
    isLoading,
    isRefetching,
    state.mounted,
    tourneyData,
  ]);

  // clean state
  useEffect(() => {
    return () => {
      setState(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs: DefaultTabsProps['tabs'] = [
    { name: GENERICS_WORDS.tdaClassic, disabled: true },
    {
      name: GENERICS_WORDS.tdaPremio,
      disabled: false,
    },
  ];

  const onHandleTabsChanges = (tab: TabType) => {
    handleTabChanges(tab);
    navigate(ROUTES.friendsTournamentPrizes);
  };

  // refresco pendientes de aceptacion al invitar postulacion
  const onHandleAfterInvitation = useCallback(
    async (pendings = false) => {
      const refreshData = await getInitData();
      if (refreshData?.estado) {
        if (pendings) {
          setState((prevState) => ({
            ...prevState,
            pendingList: refreshData.torneos_amigo.pendientesAcDuenio,
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            pendingList: refreshData.torneos_amigo.pendientesAcDuenio,
            playingList: refreshData.torneos_amigo.participando,
          }));
        }
        handleSetTourney({
          ...tourneyData,
          tourneysPending: refreshData.torneos_amigo.pendientesAcDuenio,
          tourneysPlaying: refreshData.torneos_amigo.participando,
        });
      } else {
        toast.show(TEXTS.defaultError);
      }
    },
    [handleSetTourney, toast, tourneyData]
  );

  // effecto escucha al enviar postulacion
  useEffect(() => {
    if (tourneyData.idsPendings.length > 0 && state.mounted) {
      onHandleAfterInvitation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  // effecto escucha al aceptar invitaciones
  useEffect(() => {
    if (tourneyData.idsPlayings.length > 0 && state.mounted) {
      onHandleAfterInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPlayings]);

  return (
    <>
      <Loader isVisible={!state.mounted || isLoading || isRefetching} />
      <Layout
        actionFooter={{
          actionHandler: () => {
            navigate(ROUTES.createFriendsTournament);
          },
          buttonName: 'Crear nuevo torneo',
          buttonDisabled: isLastMatchDay,
        }}
        adsSections={SECTION_NAME_FOR_ADS.torneodeamigos}
        className="px-4"
        main={() => (
          <div className="lg:pr-[111px] lg:min-h-[600px]">
            <MainContent
              isLastMatchDay={isLastMatchDay}
              isMounted={state.mounted}
              pendingList={state.pendingList}
              playingList={state.playingList}
              previousList={state.previousTourneys}
              refetchFather={onHandleAfterInvitation}
            />
          </div>
        )}
        mobile={{
          leftContent: {
            showMenuAction: true,
            items: true,
          },
          centerContent: { title: 'Torneos de Amigos' },
        }}
        sidebar={() => (
          <div className="lg:w-[334px]">
            {Boolean(
              state.pendingList.length ||
                state.playingList.length ||
                state.previousTourneys.length
            ) && (
              <Divider
                className="mt-6 mb-7 h-[2px] lg:hidden"
                type="horizontal"
              />
            )}
            <SideBarContent
              isLastMatchDay={isLastMatchDay}
              list={state.recommendedList}
            />
          </div>
        )}
        tabs={() =>
          initData?.showTDAP ? (
            <Tabs activeTab={tab} tabs={tabs} onClick={onHandleTabsChanges} />
          ) : undefined
        }
      />
    </>
  );
};
